import { subscribeAlmsyncCommon } from "@/backend/natsstubs/almsync/common/subscribe";
import { subscribeBoard } from "@/backend/natsstubs/board/subscribe";
import { subscribeConfidenceVote } from "@/backend/natsstubs/confidence_vote/subscribe";
import { subscribeSession } from "@/backend/natsstubs/session/subscribe";
import { subscribeStickyType } from "@/backend/natsstubs/sticky_type/subscribe";
import { emitStickyChanged } from "@/composables/useEventBus";
import { captureMessage } from "@/error/sentry";
import type { Board, BoardWithObjectives } from "@/model/board";
import { hasBoardObjectives } from "@/model/board";
import { relativeCoord } from "@/model/coordinates";
import type { SessionAlmStatus } from "@/model/session";
import type { Shape } from "@/model/shape";
import type { TimerEvent } from "@/model/timer";
import { isBackendUserId } from "@/model/user";
import { useArtStore } from "@/store/art";
import { useBoardsStore } from "@/store/boards";
import { useCardStore } from "@/store/card";
import { useConfidenceVoteStore } from "@/store/confidenceVote";
import { useConnectionStore } from "@/store/connection";
import { useIterationStore } from "@/store/iteration";
import { useLinkStore } from "@/store/link";
import { useModalStore } from "@/store/modal";
import { useObjectiveStore } from "@/store/objective";
import { usePointerStore } from "@/store/pointer";
import { useServerSettingsStore } from "@/store/serverSettings";
import { useSessionStore } from "@/store/session";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTimerStore } from "@/store/timer";
import { useUsersOnBoardStore } from "@/store/usersOnBoard";
import { useGlobalI18n } from "@/translations/i18n";
import { isFeatureEnabled, parseUrlWithoutRouter } from "@/utils/env/feature";

import { loadUser } from "../http/user";
import { mapApiAlmStatus } from "../impl/mapApiSession";
import type {
  ServerEvent,
  ServerIterationSubsState,
  ServerIterationUpdate,
  ServerLink,
  ServerObjective,
  ServerSettings,
  ServerSticky,
  ServerStickyType,
  ServerUserId,
} from "../serverModel";
import type { BackendReadSession } from "./BackendReadSession";
import type { EventInfo } from "./EventInfo";
import { addApiSticky, addSticky, changeApiSticky, changeSticky } from "./changeSticky";
import {
  mapApiLink,
  mapApiStickyType,
  mapApiShape,
  mapApiIterationUpdate,
  mapApiEvents,
  mapApiSettings,
  mapApiObjective,
} from "./mapApiData";
import {
  mapEvents,
  mapIterationUpdate,
  mapLink,
  mapSetting,
  mapStickyType,
} from "./mapBackendData";
import { mapObjective, mapPartialObjective } from "./mapObjective";

const { t } = useGlobalI18n();

export function createSessionSubscriptions(backendSess: BackendReadSession) {
  const subscriptions = backendSess.subscriptionNames("session");
  if (subscriptions.length !== 0) {
    captureMessage("Subscriptions not empty", { info: { level: "session", subscriptions } });
  }

  if (useNats()) {
    backendSess.apiSubscribe(subscribeConfidenceVote, {
      sessionsCreated(event) {
        useConfidenceVoteStore().onSessionCreated(event, openSession);
      },
      sessionDeleted(event) {
        useConfidenceVoteStore().removeSession(event.votingSessionId);
      },
    });
  } else {
    useConfidenceVoteStore().setupSubscriptions(backendSess, openSession);
  }

  function openSession(sessionId: string) {
    const modalStore = useModalStore();
    if (modalStore.isModalOpen("ConfidenceVoteSessionModal")) {
      modalStore.changeAttrs({ votingSessionId: sessionId });
      return;
    }
    modalStore.open("ConfidenceVoteSessionModal", { attrs: { votingSessionId: sessionId } });
  }

  if (useNats()) {
    backendSess.apiSubscribe(subscribeStickyType, {
      stickyTypeCreated(event) {
        useStickyTypeStore().addStickyType(mapApiStickyType(event));
      },
      stickyTypeUpdated(event) {
        const type = mapApiStickyType(event);
        useStickyTypeStore().updateStickyType(type);
        useCardStore().updateStickyType(type);
      },
      stickyTypeDeleted(event) {
        useStickyTypeStore().removeStickyType(event);
      },
    });
  } else {
    backendSess.sessionSubscribe<[ServerStickyType]>("sticky_type.create", (args) => {
      const [stickyType] = args;
      const type = mapStickyType(stickyType);
      useStickyTypeStore().addStickyType(type);
    });
    backendSess.sessionSubscribe<[ServerStickyType]>("sticky_type.update", (args) => {
      const [stickyType] = args;
      const type = mapStickyType(stickyType);
      useStickyTypeStore().updateStickyType(type);
      useCardStore().updateStickyType(type);
    });
    backendSess.sessionSubscribe<[string]>("sticky_type.delete", (args) => {
      const [id] = args;
      useStickyTypeStore().removeStickyType(id);
    });
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeSession, {
      settingUpdated(event) {
        useServerSettingsStore().$patch(mapApiSettings(event));
      },
      linkCreated(event) {
        useLinkStore().add(mapApiLink(event));
      },
      linkDeleted(event) {
        useLinkStore().remove(event);
      },
    });
  } else {
    backendSess.sessionSubscribe<[keyof ServerSettings, ServerSettings[keyof ServerSettings]]>(
      "setting.update",
      (args) => {
        const [key, value] = args;
        useServerSettingsStore().$patch(mapSetting(key, value!));
      },
    );
    backendSess.sessionSubscribe<[ServerLink]>("link.add", (args) => {
      const [link] = args ?? [];
      useLinkStore().add(mapLink(link));
    });
    backendSess.sessionSubscribe<[string]>("link.remove", (args) => {
      const [id] = args ?? [];
      useLinkStore().remove(id);
    });
  }
  const almSyncId = backendSess.getAlmSyncId();
  if (useNats() && almSyncId) {
    backendSess.apiSubscribe(
      subscribeAlmsyncCommon,
      {
        runningStateUpdated(event) {
          const status = mapApiAlmStatus(event.status);
          useConnectionStore().setAlm(status === "running");
          useSessionStore().setSessionAlmStatus(status);
        },
        iterationSyncUpdated(event) {
          // Flat handler literal keeps the NoInfer excess-property check; the
          // legacy topic only listened with an ALM type, so gate inside.
          if (!backendSess.getAlmType()) return;
          // The shared Status enum also carries the running-state values; the
          // iteration publisher only ever sends the two sync states.
          if (event.status !== "syncing" && event.status !== "synced") return;
          setIterationStatus({
            team_id: event.teamId,
            pi_iteration_id: event.piIterationId,
            status: event.status,
            error: event.error ?? "",
          });
        },
      },
      { sessionId: almSyncId },
    );
  } else {
    backendSess.almSubscribe<[SessionAlmStatus]>("running_state.update", (args) => {
      const [status] = args ?? [];
      useConnectionStore().setAlm(status === "running");
      useSessionStore().setSessionAlmStatus(status);
    });
    if (backendSess.getAlmType()) {
      backendSess.almSubscribe<[ServerIterationSubsState]>("iteration_sync.update", (args) => {
        const [iterState] = args ?? [];
        setIterationStatus(iterState);
      });
    }
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeBoard, {
      iterationUpdated(event, { boardId }) {
        useIterationStore().updateIteration(boardId, event.boardIterationId, (iter) =>
          mapApiIterationUpdate(event, iter),
        );
      },
    });
  } else {
    backendSess.sessionSubscribe<[string, number, ServerIterationUpdate]>(
      "iteration.update",
      (args) => {
        const [boardId, iteration, update] = args ?? [];
        useIterationStore().updateIteration(boardId, iteration, (iter) =>
          mapIterationUpdate(update, iter),
        );
      },
    );
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeSession, {
      timersUpdated(event) {
        void setTimerEvents(mapApiEvents(event.timerEvents));
      },
    });
  } else {
    backendSess.sessionSubscribe<[ServerEvent[]]>("timer.handle_event", (args) => {
      const [events] = args ?? [];
      void setTimers(events);
    });
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeBoard, {
      stickyCreated(event, { boardId, stickyId }) {
        const board = useBoardsStore().boards[boardId];
        if (board) {
          addApiSticky(event);
          emitStickyChanged(stickyId);
        }
      },
      stickyUpdateStarted(_event, { stickyId }, context) {
        useCardStore().readOnly({ readOnly: true, id: stickyId, userId: context.userId });
      },
      stickyUpdated(event, { boardId, stickyId }) {
        const board = useBoardsStore().boards[boardId];
        if (board) {
          changeApiSticky(board, stickyId, event);
          emitStickyChanged(stickyId);
        }
      },
      stickyUpdateEnded(event, { boardId, stickyId }) {
        useCardStore().readOnly({ readOnly: false, id: stickyId });
        const board = useBoardsStore().boards[boardId];
        if (board) {
          changeApiSticky(board, stickyId, event);
          emitStickyChanged(stickyId);
        }
      },
      stickyDeleted(_event, { boardId, stickyId }) {
        useCardStore().delete({ id: stickyId, boardId });
      },
    });
  } else {
    backendSess.boardSubscribe<[ServerSticky]>("", "sticky.add", "session", (args, _, event) => {
      const [data] = args ?? [];
      const board = getBoardFromEvent(event);
      if (board) {
        addSticky(data);
        emitStickyChanged(data.id);
      }
    });
    backendSess.boardSubscribe<[string, ServerUserId?]>(
      "",
      "sticky.start_update",
      "session",
      (args, _kwargs, event) => {
        const [id, data] = args ?? [];
        // event from backend: event.userId: 'backend', data.user_id: <user-id>
        // event from pi-web:                <user-id>                undefined
        const userId = data?.user_id || event.userId;
        useCardStore().readOnly({ readOnly: true, id, userId });
      },
    );
    backendSess.boardSubscribe<[string, Partial<ServerSticky>]>(
      "",
      "sticky.update",
      "session",
      (args, _kwargs, event) => {
        const [id, data] = args ?? [];
        const board = getBoardFromEvent(event);
        if (board) {
          changeSticky(board, id, data);
          emitStickyChanged(id);
        }
      },
    );
    backendSess.boardSubscribe<[string, Partial<ServerSticky>]>(
      "",
      "sticky.stop_update",
      "session",
      (args, _kwargs, event) => {
        const [id, data] = args ?? [];
        useCardStore().readOnly({ readOnly: false, id });
        const board = getBoardFromEvent(event);
        if (board) {
          changeSticky(board, id, data);
          emitStickyChanged(id);
        }
      },
    );
    backendSess.boardSubscribe<[string]>("", "sticky.remove", "session", (args, _, event) => {
      const [id] = args ?? [];
      useCardStore().delete({ id, boardId: event.boardId });
    });
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeBoard, {
      scaleUpdated(event, { boardId }) {
        useBoardsStore().setCardSize({ boardId, factor: event });
      },
      topStickyUpdated(event, { boardId }) {
        useBoardsStore().cardToFront({ id: event, boardId });
      },
    });
  } else {
    backendSess.boardSubscribe<[number]>("", "sticky.scale", "session", (args, _, event) => {
      const [factor] = args ?? [];
      useBoardsStore().setCardSize({ boardId: event.boardId, factor });
    });
    backendSess.boardSubscribe<[string]>(
      "",
      "sticky.bring_to_front",
      "session",
      (args, _, event) => {
        const [id] = args ?? [];
        useBoardsStore().cardToFront({ id, boardId: event.boardId });
      },
    );
  }
  if (useNats()) {
    backendSess.apiSubscribe(subscribeBoard, {
      shapeCreated(event, { boardId }) {
        void useBoardsStore().addShape(boardId, mapApiShape(event));
      },
      shapeUpdated(event, { boardId }) {
        useBoardsStore().editShape(boardId, mapApiShape(event));
      },
      shapeDeleted(event, { boardId }) {
        useBoardsStore().removeShape(boardId, event);
      },
    });
  } else {
    backendSess.boardSubscribe<[Shape]>(
      "",
      "shape.add",
      "session",
      ([shape], _, event) => void useBoardsStore().addShape(event.boardId, shape),
    );
    backendSess.boardSubscribe<[Shape]>("", "shape.edit", "session", ([shape], _, event) =>
      useBoardsStore().editShape(event.boardId, shape),
    );
    backendSess.boardSubscribe<[string]>("", "shape.remove", "session", ([id], _, event) =>
      useBoardsStore().removeShape(event.boardId, id),
    );
  }
}

export function createBoardSubscriptions(backendSess: BackendReadSession, board: Board) {
  boardSubscriptions();
  teamBoardSubscriptions();
  objectivesBoardSubscriptions();

  function boardSubscriptions() {
    const subscriptions = backendSess.subscriptionNames("board");
    if (subscriptions.length !== 0) {
      captureMessage("Subscriptions not empty", { info: { level: "board", subscriptions } });
    }
    backendSess.boardSubscribe<[{ x: number; y: number }]>(
      board.id,
      "pointer.position",
      "board",
      (args, _, event) => {
        const [coord] = args ?? [];
        usePointerStore().set({
          boardId: board.id,
          id: event.userId,
          pos: relativeCoord(coord.x, coord.y),
        });
      },
    );
    backendSess.boardSubscribe<[string]>(board.id, "subscribers.join", "board", (args) => {
      const [userId] = args ?? [];
      // don't await!
      void handleUserJoined(userId);
    });
    backendSess.boardSubscribe<[string]>(board.id, "subscribers.leave", "board", (args) => {
      const [userId] = args ?? [];
      handleUserLeft(userId);
    });

    async function handleUserJoined(userId: string) {
      if (!isBackendUserId(userId)) {
        const user = useUsersOnBoardStore().findUser(userId);
        if (user) {
          user.boardVisitTimestamp = Date.now();
        } else {
          const user = await loadUser({ id: userId, caller: "boardUser" });
          const boardVisitTimestamp = Date.now();
          useUsersOnBoardStore().addUser({ ...user, boardVisitTimestamp });
        }
      }
    }

    function handleUserLeft(userId: string) {
      if (!isBackendUserId(userId)) {
        useUsersOnBoardStore().removeUser(userId);
      }
    }
  }

  function objectivesBoardSubscriptions() {
    if (board.type === "objectives") {
      Object.values(useBoardsStore().boards)
        .filter((board) => useArtStore().isCurrentArt(board.artId))
        .filter((board) => hasBoardObjectives(board))
        .forEach((board) => objectivesSubscriptions(board));
    }
  }

  function teamBoardSubscriptions() {
    if (board.type === "team") {
      objectivesSubscriptions(board);
    }
  }

  function objectivesSubscriptions(board: BoardWithObjectives) {
    const objectives = useObjectiveStore();
    if (useNats()) {
      backendSess.apiSubscribe(
        subscribeBoard,
        {
          objectiveCreated(event, { objectiveId }) {
            objectives.add(
              { boardId: board.id, id: objectiveId, ...mapApiObjective(event) },
              !event.isStretch,
            );
          },
          objectiveDeleted(_, { objectiveId }) {
            objectives.remove({ boardId: board.id, id: objectiveId });
          },
          objectiveUpdated(event, { objectiveId }) {
            if (event.objective) {
              objectives.update({
                boardId: board.id,
                id: objectiveId,
                ...mapApiObjective(event.objective),
              });
            }
            if (event.place) {
              objectives.move({
                boardId: board.id,
                id: objectiveId,
                stretch: event.place.isStretch,
                rank: event.place.position,
              });
            }
          },
        },
        { boardId: board.id },
        "board",
      );
    } else {
      backendSess.boardSubscribe<[ServerObjective]>(board.id, "objective.add", "board", (args) => {
        const [objective] = args ?? [];
        objectives.add(
          { boardId: board.id, ...mapObjective(objective) },
          objective.is_stretch !== true,
        );
      });
      backendSess.boardSubscribe<[string]>(board.id, "objective.remove", "board", (args) => {
        const [id] = args ?? [];
        objectives.remove({ boardId: board.id, id });
      });
      backendSess.boardSubscribe<[string, ServerObjective]>(
        board.id,
        "objective.update",
        "board",
        (args) => {
          const [id, objective] = args ?? [];
          objectives.update({ boardId: board.id, ...mapPartialObjective(objective), id });
        },
      );
      backendSess.boardSubscribe<[string, boolean, number]>(
        board.id,
        "objective.move",
        "board",
        (args) => {
          const [id, stretch, rank] = args ?? [];
          objectives.move({ boardId: board.id, id, stretch, rank });
        },
      );
    }
  }
}

function setIterationStatus(iterState: ServerIterationSubsState) {
  for (const id in useBoardsStore().boards) {
    const board = useBoardsStore().boards[id];
    if (board.type === "team" && board.team.id === "" + iterState.team_id) {
      const status =
        iterState.status === "syncing" ? "syncing" : iterState.error ? "fail" : "success";
      useBoardsStore().setIterationStatus({
        boardId: id,
        id: iterState.pi_iteration_id,
        status,
        detail: iterState.error
          ? t("iterationSync.failedSync", { errorMessage: iterState.error })
          : "",
      });
      break;
    }
  }
}

export async function setTimers(events: ServerEvent[]) {
  await setTimerEvents(mapEvents(events));
}

async function setTimerEvents(timers: TimerEvent[]) {
  await loadTimerUsers(timers);
  useTimerStore().setTimers(timers);
}

function loadTimerUsers(timers: TimerEvent[]) {
  return Promise.all(
    timers.map(async (timer) => {
      timer.updatedByUser = await loadUser({ id: timer.updatedById, caller: "timerUsers" });
    }),
  );
}

function getBoardFromEvent(event: EventInfo) {
  return useBoardsStore().boards[event.boardId];
}

function useNats() {
  return isFeatureEnabled({ query: parseUrlWithoutRouter() }, "nats-subscribe");
}
